import React, { useContext, useEffect, useRef, useState } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBook, faCamera, faImage, faUsers, faWandSparkles } from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"
import Container from "../atoms/Container"
import CoachAvatar from "../atoms/CoachAvatar"
import SchoolAvatar from "../school/SchoolAvatar"
import PictureCropper from "../components/PictureCropper"
import { Block, StepTransition, WideButton } from "../school/SheetBlocks"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { callFunction } from "../services/firebase.service"
import { openImage, renderLogo } from "../utils/logo.utils"
import { networkAwareMessage } from "../utils/network.utils"

// Le parole del foglio, per la foto del maestro e per il logo del circolo:
// la stessa cosa detta al femminile e al maschile.
const WORDS = {
	coach: {
		title: "La tua foto",
		subtitle: () => "La vedono i colleghi, accanto al tuo nome.",
		context: "Così ti vedono",
		empty: "Senza foto, i colleghi vedono le tue iniziali.",
		choose: "Scegli una foto",
		remove: "Togli la foto",
		removing: "Tolgo la foto...",
		saved: "Foto aggiornata",
		removed: "Foto tolta",
		cropTitle: "Sistemala nel cerchio",
		cropSubtitle: "Trascinala per spostarla, allarga due dita per ingrandirla.",
		heavy: "La foto",
		failed: "Non sono riuscito a salvare la foto",
		// la fotocamera davanti: una foto di sé
		capture: "user",
		cropper: {
			image: "La foto da ritagliare",
			zoom: "Ingrandisci la foto",
			saving: "Salvo la foto...",
			confirm: "Usa questa",
		},
	},
	school: {
		title: "Il logo del circolo",
		subtitle: name => `Lo vedono tutti i maestri di ${name}.`,
		context: "In cima allo schermo",
		empty: "Senza logo, in cima allo schermo il circolo è la sua iniziale.",
		choose: "Scegli un'immagine",
		remove: "Togli il logo",
		removing: "Tolgo il logo...",
		saved: "Logo aggiornato",
		removed: "Logo tolto",
		cropTitle: "Sistemalo nel cerchio",
		cropSubtitle: "Trascinalo per spostarlo, allarga due dita per ingrandirlo.",
		heavy: "Il logo",
		failed: "Non sono riuscito a salvare il logo",
		// la fotocamera dietro: il cartello all'ingresso, la maglia
		capture: "environment",
		cropper: {
			image: "Il logo da ritagliare",
			zoom: "Ingrandisci il logo",
			saving: "Salvo il logo...",
			confirm: "Usa questo",
		},
	},
}

// ──────────────────────────────────────────────────────────────────────────
// LA FOTO DEL MAESTRO E IL LOGO DEL CIRCOLO
//
// Un foglio tutto suo, lo stesso per tutti e due, in due passi:
//
// 1. com'è adesso, in grande, e sotto dove la vedono gli altri (la barra in
//    basso, la pillola del maestro, l'elenco; per il logo la fascia in
//    cima). Da lì si sceglie un'immagine, se ne scatta una, o la si toglie.
// 2. l'immagine scelta si sistema nel cerchio, rivedendola già piccola
//    com'era nell'app; "Usa questa" la salva subito.
//
// Prima foto e logo si cambiavano con due righe di testo piccole dentro la
// scheda dell'account o i dati del circolo, e il ritaglio si apriva lì,
// stretto: ora hanno un posto loro.
//
// La foto la scrive `updateCoachPhoto`, il logo `updateSchool`: tutti e due
// stanno sul loro documento, che l'app già legge (vedi docs/interfaccia.md,
// La foto del maestro). `back` è il foglio da cui si è arrivati, se ce n'è
// uno: finito qui si torna lì invece di chiudere.
// ──────────────────────────────────────────────────────────────────────────
export function PictureModal({ closeModal, kind = "coach", back }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { getSelectedSchool, selectedSchool, updateSchool } = useContext(schoolContext)

	const words = WORDS[kind]
	const isCoach = kind === "coach"
	const school = getSelectedSchool()
	const picture = isCoach ? coach?.photo : school?.logo

	const gallery = useRef(null)
	const camera = useRef(null)
	// l'immagine scelta, finché la si sistema nel cerchio
	const [picked, setPicked] = useState(null)
	const [busy, setBusy] = useState(null)

	// uscendo a metà l'immagine aperta si chiude
	useEffect(() => () => picked?.close(), [picked])

	const finish = () => (back ? Modal.open("schoolSection", back) : closeModal())

	const save = async (value, doing) => {
		setBusy(doing)
		try {
			if (isCoach) {
				const updateCoachPhoto = callFunction("updateCoachPhoto")
				await updateCoachPhoto({ photo: value })
			} else {
				await updateSchool(selectedSchool, { logo: value })
			}
			toast.success(value ? words.saved : words.removed, { position: "top-center" })
			finish()
		} catch (err) {
			toast.error(networkAwareMessage(err, words.failed), { position: "top-center" })
			setBusy(null)
		}
	}

	const pick = async event => {
		const file = event.target.files?.[0]
		// la stessa immagine scelta due volte deve ricontare
		event.target.value = ""
		if (!file) return
		try {
			setPicked(await openImage(file))
		} catch (err) {
			toast.error(err.message, { position: "top-center" })
		}
	}

	const confirmCrop = (crop, view) => {
		let value
		try {
			value = renderLogo(picked, crop, view, words.heavy)
		} catch (err) {
			toast.error(err.message, { position: "top-center" })
			return
		}
		save(value, words.cropper.saving)
	}

	// un logo è disegnato per il bianco; una foto sta sul tondo delle iniziali
	const circle = isCoach ? theme.colors.gunmetal_lighter : "#ffffff"

	if (picked) {
		return (
			<Container>
				<StepTransition stepKey="crop" direction={1}>
					<Text textSize="title">{words.cropTitle}</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{words.cropSubtitle}
					</Text>
					<PictureCropper
						picked={picked}
						isSaving={busy !== null}
						onCancel={() => setPicked(null)}
						onConfirm={confirmCrop}
						words={words.cropper}
						circle={circle}
						cover={isCoach}
					/>
				</StepTransition>
			</Container>
		)
	}

	return (
		<Container>
			<StepTransition stepKey="choose" direction={0}>
				<Text textSize="title">{words.title}</Text>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					{words.subtitle(school?.schoolName ?? "")}
				</Text>

				{/* com'è adesso, in grande */}
				<Div d="flex" justify="center" m={{ t: "1.25rem", b: "1.25rem" }}>
					<span
						style={{
							display: "flex",
							borderRadius: "50%",
							boxShadow: `0 0 0 6px ${theme.colors.surface}`,
						}}>
						{isCoach ? (
							<CoachAvatar coach={coach} size={132} />
						) : (
							<SchoolAvatar school={school} size={132} />
						)}
					</span>
				</Div>

				{/* e dove la vedono gli altri */}
				<Block p="0.75rem 0.875rem">
					<Text textSize="caption" textWeight="bold" textColor={theme.colors.gunmetal} m={{ b: "0.6rem" }}>
						{words.context}
					</Text>
					{isCoach ? <CoachPlaces coach={coach} /> : <SchoolPlace school={school} />}
					{!picture && (
						<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.6rem" }}>
							{words.empty}
						</Text>
					)}
				</Block>

				<Div d="flex" flexDir="column" m={{ t: "1rem" }} style={{ gap: "0.625rem" }}>
					<WideButton
						icon={faImage}
						text={words.choose}
						bg={theme.colors.primary}
						color={theme.colors.white}
						shadow={theme.shadows.accent}
						onClick={() => !busy && gallery.current?.click()}
					/>
					<WideButton
						icon={faCamera}
						text="Scatta una foto"
						bg={theme.colors.surface}
						color={theme.colors.gunmetal}
						onClick={() => !busy && camera.current?.click()}
					/>
					{picture && (
						<button
							type="button"
							onClick={() => !busy && save(null, words.removing)}
							style={{
								height: "2.75rem",
								border: 0,
								background: "transparent",
								color: busy ? theme.colors.text_light : theme.colors.red,
								font: "inherit",
								fontSize: "0.875rem",
								cursor: "pointer",
							}}>
							{busy ?? words.remove}
						</button>
					)}
				</Div>

				<input ref={gallery} type="file" accept="image/*" hidden onChange={pick} />
				{/* sul telefono apre direttamente la fotocamera */}
				<input ref={camera} type="file" accept="image/*" capture={words.capture} hidden onChange={pick} />
			</StepTransition>
		</Container>
	)
}

// Dove i colleghi vedono la foto: la barra in basso, la pillola arancione
// del maestro che si guarda, una riga dell'elenco dei maestri. In piccolo,
// con i colori veri.
function CoachPlaces({ coach }) {
	const firstName = (coach?.coachName ?? "").split(" ")[0]
	const pill = {
		display: "flex",
		alignItems: "center",
		background: theme.colors.background,
		borderRadius: "999px",
	}

	return (
		<Div aria-hidden="true" d="flex" flexDir="column" style={{ gap: "0.6rem" }}>
			<Div d="flex" align="center" justify="space-between" style={{ gap: "0.5rem" }}>
				<Div d="flex" align="center" style={{ gap: "0.3rem" }}>
					<span style={{ ...pill, height: "34px", padding: "0 0.75rem", gap: "0.75rem" }}>
						{[faUsers, faWandSparkles, faBook].map((icon, i) => (
							<FontAwesomeIcon key={i} icon={icon} color={theme.colors.gunmetal} style={{ fontSize: "0.8rem" }} />
						))}
					</span>
					<span style={{ ...pill, width: "38px", height: "38px", justifyContent: "center" }}>
						<CoachAvatar coach={coach} size={28} initials={1} />
					</span>
				</Div>
				<span
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.35rem",
						height: "30px",
						padding: "0 0.65rem 0 3px",
						borderRadius: "15px",
						background: theme.colors.primary,
						color: theme.colors.white,
						fontSize: "0.75rem",
					}}>
					<CoachAvatar coach={coach} size={24} style={{ boxShadow: `0 0 0 1.5px ${theme.colors.white}` }} />
					{firstName}
				</span>
			</Div>
			<Div d="flex" align="center" style={{ gap: "0.45rem" }}>
				<CoachAvatar coach={coach} size={26} />
				<Text textSize="caption" textWeight="bold">
					{coach?.coachName}
				</Text>
			</Div>
		</Div>
	)
}

// Dove si vede il logo: il tondo a destra della fascia arancione in cima.
function SchoolPlace({ school }) {
	return (
		<Div
			aria-hidden="true"
			d="flex"
			align="center"
			justify="space-between"
			h="44px"
			p={{ l: "0.875rem", r: "4px" }}
			bg="primary"
			style={{ borderRadius: "22px" }}>
			<img src={require("../images/incampo_logo_header.png")} alt="" style={{ height: "18px" }} />
			<span
				style={{
					display: "flex",
					padding: "2px",
					borderRadius: "50%",
					background: "rgba(255, 255, 255, 0.22)",
				}}>
				<SchoolAvatar school={school} size={32} tone="onPrimary" />
			</span>
		</Div>
	)
}

export default PictureModal
